import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    showFlowOfFundsCycleOutcomeToast,
    showFlowOfFundsSavingToast,
} from '../flowOfFundsAutoSaveToast';

export const CONSIDERATION_AUTOSAVE_TOAST_CONTAINER_ID =
    'purchase-price-consideration-autosave-toast-container';

const CONSIDERATION_AUTOSAVE_TOAST_ID = 'purchase-price-consideration-save';

export type ConsiderationSaveOutcome = 'ok' | 'conflict' | 'error';

export interface ConsiderationAutoSaveReporter {
    reportStart: () => void;
    reportOutcome: (outcome: ConsiderationSaveOutcome) => void;
}

const ConsiderationAutoSaveContext = createContext<ConsiderationAutoSaveReporter | null>(null);

const NOOP_REPORTER: ConsiderationAutoSaveReporter = {
    reportStart: () => {},
    reportOutcome: () => {},
};

export const useConsiderationAutoSaveReporterState = (): ConsiderationAutoSaveReporter => {
    const isMountedRef = useRef(true);
    const inFlightCountRef = useRef(0);
    const hasErrorRef = useRef(false);
    const hasConflictRef = useRef(false);
    const savedCountRef = useRef(0);

    const reportStart = useCallback(() => {
        inFlightCountRef.current += 1;
        if (inFlightCountRef.current !== 1) {
            return;
        }
        hasErrorRef.current = false;
        hasConflictRef.current = false;
        savedCountRef.current = 0;
        if (isMountedRef.current) {
            showFlowOfFundsSavingToast(
                CONSIDERATION_AUTOSAVE_TOAST_CONTAINER_ID,
                CONSIDERATION_AUTOSAVE_TOAST_ID
            );
        }
    }, []);

    const reportOutcome = useCallback((outcome: ConsiderationSaveOutcome) => {
        if (outcome === 'error') {
            hasErrorRef.current = true;
        } else if (outcome === 'conflict') {
            hasConflictRef.current = true;
        } else {
            savedCountRef.current += 1;
        }
        inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
        if (inFlightCountRef.current > 0) {
            return;
        }
        if (isMountedRef.current) {
            showFlowOfFundsCycleOutcomeToast(
                CONSIDERATION_AUTOSAVE_TOAST_CONTAINER_ID,
                CONSIDERATION_AUTOSAVE_TOAST_ID,
                hasErrorRef.current,
                hasConflictRef.current,
                savedCountRef.current
            );
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            toast.dismiss(CONSIDERATION_AUTOSAVE_TOAST_ID);
        };
    }, []);

    return useMemo(() => ({ reportStart, reportOutcome }), [reportStart, reportOutcome]);
};

export const ConsiderationAutoSaveProvider = ({
    reporter,
    children,
}: {
    reporter: ConsiderationAutoSaveReporter;
    children: React.ReactNode;
}) => (
    <ConsiderationAutoSaveContext.Provider value={reporter}>
        {children}
    </ConsiderationAutoSaveContext.Provider>
);

export const useConsiderationAutoSaveReporter = (): ConsiderationAutoSaveReporter =>
    useContext(ConsiderationAutoSaveContext) ?? NOOP_REPORTER;
